// ─────────────────────────────────────────────────────────────────────────────

import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';
import {
  usePaymentStatePollingMachine,
  PaymentStatePollingActors,
} from '@sg/shared-fe-features-ordering';

import { useFeatureFlag } from '@order/LaunchDarkly';

export const useGiftCardPollingMachine = (params: UseGiftCardPollingParams) => {
  const {
    fetchGiftCardPaymentState,
    cancelGiftCardPayment,
    clearLocalStorageOrderId,
    incrementPurchaseAttempt,
  } = params;

  // ─── Payment State ─────────────────────────────────────────────────────────

  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config');

  const {
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
  } = getGiftCardPaymentStateConfig(orderPaymentStateConfig);

  // ─── Error Handling ────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const onGiftCardPaymentStateError = useCallback(
    (params: { context: { paymentState: { paymentStateError: string | null | undefined } } }) => {
      const error = params.context.paymentState?.paymentStateError ?? formatMessage(messages.error);
      addNoticeBanner({ text: error, palette: 'caution' });
    },
    [addNoticeBanner, formatMessage],
  );

  // ─── Payment State Machine ─────────────────────────────────────────────────

  return usePaymentStatePollingMachine({
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
    fetchPaymentState: fetchGiftCardPaymentState,
    cancelPayment: cancelGiftCardPayment,
    clearPendingPayment: clearLocalStorageOrderId,
    incrementPurchaseAttempt: incrementPurchaseAttempt,
    onPaymentStateError: onGiftCardPaymentStateError,
  });
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Gift Card Polling Machine | Payment State Error',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getGiftCardPaymentStateConfig = (config?: Record<string, number>) => {
  return {
    paymentStatePollingInterval: getNumberOrNull(config?.pollingInterval) || 3000,
    paymentStateExpirationTolerance: getNumberOrNull(config?.expirationTolerance) || 1000 * 60 * 2,
    paymentStateDelayedTolerance: getNumberOrNull(config?.delayedTolerance) || 12_000,
  };
};

const getNumberOrNull = (value?: string | number) => {
  return Number.isNaN(Number(value)) ? null : Number(value);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseGiftCardPollingParams = {
  fetchGiftCardPaymentState: typeof PaymentStatePollingActors.fetchPaymentState;
  cancelGiftCardPayment: typeof PaymentStatePollingActors.cancelPayment;
  clearLocalStorageOrderId: () => void;
  incrementPurchaseAttempt: () => void;
};
